/**
 * useMediaQuery — does the viewport match `query` right now, and follow it as it changes.
 *
 * `false` where `matchMedia` does not exist (jsdom, SSR): that is a value, not a failure, and
 * every consumer must treat it as the conservative side — the layout that needs no room.
 *
 * Written for the Deckungsbeitrag drawer (2026-09-07), which moves its meta line and its
 * Bezugsgröße switcher onto the drawer's title row only where that row has the width.
 */
import { useEffect, useState } from 'react';

const query_ = (query: string): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => query_(query)?.matches ?? false);

  useEffect(() => {
    const mql = query_(query);
    if (!mql) return undefined;
    setMatches(mql.matches);
    const onChange = (e: { matches: boolean }) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
